import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApi } from "../useApi";
import {
  MILESTONE_TYPES,
  type DetectedMilestone,
  type MilestoneType,
  type RoastDetail,
} from "../types";
import { ErrorBox } from "../components/ErrorBox";
import { sourceLabel } from "../chart";
import { formatConfidence, formatTemp, formatTime } from "../format";

export function CorrectMilestone() {
  const { roastId = "" } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [roast, setRoast] = useState<RoastDetail | null>(null);
  const [milestones, setMilestones] = useState<DetectedMilestone[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);

  // `get_detected_milestones` carries the backend-computed effective
  // view; `roast.milestones` is the bare model dump without it.
  const reload = () => {
    setErr(null);
    Promise.all([api.getRoast(roastId), api.getDetectedMilestones(roastId)])
      .then(([r, m]) => {
        setRoast(r);
        setMilestones(m.milestones);
      })
      .catch(setErr);
  };
  useEffect(reload, [api, roastId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err && !roast) return <ErrorBox error={err} />;
  if (!roast || !milestones) return <p className="muted">Loading…</p>;

  const unit = roast.temp_unit ?? "C";
  const byType = new Map<MilestoneType, DetectedMilestone>(
    milestones.map((m) => [m.milestone_type, m]),
  );

  return (
    <div className="col">
      <div>
        <Link to={`/roasts/${encodeURIComponent(roastId)}`}>← Back</Link>
      </div>
      <h1 style={{ margin: 0 }}>Correct milestones</h1>
      <p className="muted small">
        Corrections override both the recorded marker and the sensor-derived
        estimate. They're stored as durable priors — future roasts can learn
        from them.
      </p>

      <ErrorBox error={err} />
      {ok && <div className="ok-box">{ok}</div>}

      {MILESTONE_TYPES.map((t) => {
        const m = byType.get(t);
        return (
          <CorrectionCard
            key={t}
            milestoneType={t}
            current={m}
            unit={unit}
            onSaved={(msg) => {
              setOk(msg);
              reload();
              setTimeout(() => setOk(null), 1500);
            }}
            onError={setErr}
            api={api}
            roastId={roastId}
          />
        );
      })}

      <div>
        <button
          className="secondary"
          onClick={() => navigate(`/roasts/${encodeURIComponent(roastId)}`)}
        >
          Done
        </button>
      </div>
    </div>
  );
}

interface CardProps {
  milestoneType: MilestoneType;
  current: DetectedMilestone | undefined;
  unit: "C" | "F";
  onSaved: (msg: string) => void;
  onError: (e: unknown) => void;
  api: ReturnType<typeof useApi>;
  roastId: string;
}

function CorrectionCard({
  milestoneType,
  current,
  unit,
  onSaved,
  onError,
  api,
  roastId,
}: CardProps) {
  const [time, setTime] = useState<string>("");
  const [temp, setTemp] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Seed fields with the effective values so "save" without edits is a
    // genuine confirmation of the existing estimate (corrections always win
    // over estimates downstream).
    if (current?.effective_time_s != null) setTime(String(current.effective_time_s));
    if (current?.effective_temp != null) setTemp(String(current.effective_temp));
  }, [current]);

  const save = async () => {
    setSaving(true);
    try {
      await api.correctMilestone(roastId, {
        milestone_type: milestoneType,
        corrected_time_s: time === "" ? null : Number(time),
        corrected_temp: temp === "" ? null : Number(temp),
        reason: reason || null,
      });
      onSaved(`Saved correction for ${milestoneType}.`);
    } catch (e) {
      onError(e);
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.clearMilestoneCorrection(roastId, {
        milestone_type: milestoneType,
        reason: reason || null,
      });
      onSaved(`Cleared correction for ${milestoneType}.`);
      setTime("");
      setTemp("");
    } catch (e) {
      onError(e);
    } finally {
      setSaving(false);
    }
  };

  const cf = formatConfidence(current?.effective_confidence);
  const hasCorrection = current?.effective_source === "corrected";

  return (
    <div className="card">
      <div className="row">
        <strong style={{ textTransform: "capitalize" }}>{milestoneType.replace("_", " ")}</strong>
        <span className="muted small">
          recorded {formatTime(current?.recorded_time_s)} /{" "}
          {formatTemp(current?.recorded_temp, unit)}
          {current?.recorded_time_s != null && ` (${sourceLabel(current.recorded_source)})`}
        </span>
        <span className="muted small">
          estimated {formatTime(current?.estimated_time_s)} /{" "}
          {formatTemp(current?.estimated_temp, unit)}
          {current?.estimated_time_s != null &&
            ` (detector ${formatConfidence(current.detector_confidence).label})`}
        </span>
        <span className="muted small">
          in use: {current ? sourceLabel(current.effective_source) : "–"}
        </span>
        <span className={`badge ${cf.cls}`}>{cf.label}</span>
        {hasCorrection && <span className="badge ok">corrected</span>}
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <label className="col">
          <span className="muted small">Time (s)</span>
          <input
            type="number"
            step="1"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={{ width: 100 }}
          />
        </label>
        <label className="col">
          <span className="muted small">Temp (°{unit})</span>
          <input
            type="number"
            step="0.1"
            value={temp}
            onChange={(e) => setTemp(e.target.value)}
            style={{ width: 110 }}
          />
        </label>
        <label className="col" style={{ flex: 1, minWidth: 200 }}>
          <span className="muted small">Reason (short text)</span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. audible FC at 8:42"
          />
        </label>
        <div className="col" style={{ alignSelf: "end" }}>
          <button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          {hasCorrection && (
            <button className="secondary" onClick={clear} disabled={saving}>
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
